import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, MenuItem } from '@material-ui/core';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';

import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import NgpTextField from '../../form/NgpTextField';
import NgpNativeNumberField from '../../form/NgpNativeNumberField';
import NgpSelect from '../../form/NgpSelect';
import NgpToggle from '../../form/NgpToggle';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { useFindById } from '../../../services/FormQueryService';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { AdminPages, buildPageId, buildRootPageTarget, useEntityIdParam } from '../AdminLinkUtils';
import { useNavigate } from '../../../utilities/PgmUtilities';
import {
    VOIP_CALL_MANAGER_DEFAULT_VALUES,
    VOIP_CALL_MANAGER_TYPE_OPTIONS,
    VOIP_CALL_MANAGERS_API_URL,
    VOIP_CALL_MANAGERS_ROOT,
} from './VoipConstants';

import { useDetailStyles } from './VoipCallManagersStyles';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';

export default function VoipCallManagersDetailPage() {
    const classes = useDetailStyles();
    const recordId = useEntityIdParam();
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: VOIP_CALL_MANAGER_DEFAULT_VALUES,
        mode: 'onChange',
    });
    const { handleSubmit, reset, setError, watch } = methods;
    const navigate = useNavigate();

    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const formSaveService = useFormSaveService();
    const formDeleteService = useFormDeleteService();

    const enabledSecurity = watch('enableSecurity');
    const sipProxy = watch('sipProxy');
    const { initialized, finding, formData } = useFindById({
        url: `${VOIP_CALL_MANAGERS_API_URL}/`,
        recordId,
        resetFunc: reset,
        defaultValues: VOIP_CALL_MANAGER_DEFAULT_VALUES,
    });

    // Mirrors portal/ui: auto-populate domain from sipProxy when the original domain is empty
    const isNew = !recordId;
    useEffect(() => {
        if (initialized && isNew && sipProxy) {
            methods.setValue('domain', sipProxy, { shouldDirty: true, shouldValidate: true });
        }
    }, [initialized, isNew, sipProxy, methods]);

    const handleFormSubmit = async (submitData = {}) => {
        const dataToSave = _.extend({}, formData, submitData);

        const response = await formSaveService.save({
            url: VOIP_CALL_MANAGERS_API_URL,
            recordId,
            dataToSave,
            setError,
        });

        const savedData = _.isObject(response) ? response : dataToSave;
        reset(savedData);
        navigate(VOIP_CALL_MANAGERS_ROOT);

        return response;
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit);

    const handleConfirmedDelete = () => {
        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteById(VOIP_CALL_MANAGERS_API_URL, recordId),
            onDeleteGoTo: VOIP_CALL_MANAGERS_ROOT,
        })();
    };

    let title = t`New Call Manager`;
    if (recordId) {
        title = _.isEmpty(formData?.name) ? t`Edit Call Manager` : t`Edit ${formData.name}`;
    }

    return (
        <AdminPage
            title={title}
            pageId={buildPageId(AdminPages.VoipConfig)}
            breadcrumbs={[
                { link: buildRootPageTarget(AdminPages.VoipConfig), text: t`VoIP Configuration` },
                { link: VOIP_CALL_MANAGERS_ROOT, text: t`Call Managers` },
                { text: title },
            ]}
            actions={
                <Actions>
                    <ConfirmDeleteButton
                        handleConfirmedDelete={handleConfirmedDelete}
                        plural={false}
                        disabled={!recordId}
                    />
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized}
                    loading={finding || saving || deleting}
                >
                    <FormSection title={t`Call Manager Details`}>
                        <NgpTextField
                            fieldName="name"
                            label={t`Display Name`}
                            helpTipText={t`Any desired name to uniquely identify this Call Manager`}
                            required
                            inputProps={{ maxLength: 50, size: 50 }}
                        />
                        <NgpTextField
                            fieldName="description"
                            label={t`Description`}
                            inputProps={{ maxLength: 255, size: 50 }}
                        />
                        <NgpSelect fieldName="cmType" label={t`Call Manager Type`} className={classes.select} required>
                            {VOIP_CALL_MANAGER_TYPE_OPTIONS.map((item) => (
                                <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </NgpSelect>
                        <NgpTextField
                            fieldName="sipProxy"
                            label={t`Call Manager or SIP Proxy's Hostname/IP`}
                            required
                            inputProps={{ maxLength: 50, size: 50 }}
                        />
                        <NgpTextField
                            fieldName="domain"
                            label={t`SIP Domain`}
                            helpTipText={t`Domain of SIP URI`}
                            required
                            inputProps={{ maxLength: 50, size: 50 }}
                        />
                        <NgpNativeNumberField
                            fieldName={'sipPort'}
                            label={<Trans>SIP Port</Trans>}
                            min={1}
                            max={65535}
                            required
                            inputWidth={'15ch'}
                        />
                        <NgpNativeNumberField
                            fieldName="rtpStartPort"
                            label={t`RTP Start Port`}
                            helpTipText={t`Default start port is 4000`}
                            min={1}
                            max={65535}
                            inputWidth={'15ch'}
                        />
                        <NgpNativeNumberField
                            fieldName="rtpEndPort"
                            label={t`RTP End Port`}
                            helpTipText={t`Default end port is 6000`}
                            min={1}
                            max={65535}
                            inputWidth={'15ch'}
                        />
                        <NgpToggle fieldName="enableSecurity" label={<Trans>Use Secure VoIP</Trans>} />
                        {enabledSecurity && (
                            <NgpToggle
                                fieldName="verifyCMCertificate"
                                label={<Trans>Validate Call Manager Certificate on nPoints</Trans>}
                            />
                        )}
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
